import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { differenceInCalendarDays, parseISO } from 'date-fns';
import {
  Badge,
  DatatableCell,
  DatatableRow,
  Progress,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import {
  CircleCheckIcon,
  type LucideIcon,
  MinusCircleIcon,
  TrendingDownIcon,
  TrendingUpIcon,
} from 'lucide-react';
import type { ProjectCampaign } from '@/entities/media-campaign';
import { connectorPlatformLabel, PlatformLogo } from '@/entities/media-connector';
import { EM_DASH, formatMetric } from '@/shared/utils/metric-format';
import { SpendProgress } from '../SpendProgress';
import { pacingState, type PacingState } from '../../lib/pacing-status';

export interface PacingRowProps {
  campaign: ProjectCampaign;
}

/** The label is a key, not a resolved string: this table is module scope. */
const STATE_BADGE = {
  on_track: {
    labelKey: 'performancePacing.pacingRow.onTrack',
    variant: 'positive',
    Icon: CircleCheckIcon,
  },
  under: {
    labelKey: 'performancePacing.pacingRow.underPacing',
    variant: 'warning',
    Icon: TrendingDownIcon,
  },
  over: {
    labelKey: 'performancePacing.pacingRow.overPacing',
    variant: 'destructive',
    Icon: TrendingUpIcon,
  },
  // Not a reserved status colour on purpose: this isn't a good or bad reading, it's the
  // absence of one - no budget to pace against, or the period hasn't started.
  not_pacing: {
    labelKey: 'performancePacing.pacingRow.notPacing',
    variant: 'secondary',
    Icon: MinusCircleIcon,
  },
} as const satisfies Record<
  PacingState,
  {
    labelKey: string;
    variant: 'positive' | 'warning' | 'destructive' | 'secondary';
    Icon: LucideIcon;
  }
>;

/**
 * What to show when there is no logo to show. Only a manual campaign can lack a platform,
 * since a synced one carries its connector's.
 */
function platformFallbackLabel(
  campaign: Pick<ProjectCampaign, 'platform'>,
  t: TFunction<'projects'>,
): string {
  return campaign.platform
    ? connectorPlatformLabel(campaign.platform)
    : t('performancePacing.pacingRow.manual');
}

/** Calendar days from today through the flight end; negative once a flight has ended. */
function daysRemaining(flightEndDate: string | null): number | null {
  if (!flightEndDate) return null;
  return differenceInCalendarDays(parseISO(flightEndDate), new Date());
}

/**
 * One campaign's pacing. An absent value always renders as an em dash plus a reason, never
 * a bare dash: a campaign with no linked budget has nothing to pace against, and a campaign
 * with a budget whose `pacingPct` came back null has something linked but nothing computable
 * (the API collapses several causes - no resolvable period, a non-positive amount, or the
 * period not having started - into that one null, so the copy can't point at just one of
 * them). Two different situations with their own wording, so they can't be read as the same
 * thing. Neither case renders a 0% bar or a `progressbar` at all: a zero-width bar would read
 * as "on pace at zero", a measurement nobody made.
 *
 * The state badge always carries an icon and a text label alongside its colour, so the
 * reading survives for someone who cannot use hue as the only signal.
 */
export function PacingRow({ campaign }: PacingRowProps) {
  const { t } = useTranslation('projects');
  const hasBudget = campaign.budgetIds.length > 0;
  const state = pacingState(campaign.pacingPct);
  const badge = STATE_BADGE[state];
  const remaining = daysRemaining(campaign.flightEndDate);

  return (
    <DatatableRow>
      <DatatableCell>{campaign.name}</DatatableCell>
      <DatatableCell>
        <PlatformLogo
          platform={campaign.platform}
          fallbackLabel={platformFallbackLabel(campaign, t)}
        />
      </DatatableCell>
      <DatatableCell>
        <SpendProgress
          spendToDate={campaign.spendToDate}
          projectedSpend={campaign.projectedSpend}
          currency={campaign.currency}
          campaignName={campaign.name}
        />
      </DatatableCell>
      <DatatableCell>
        {!hasBudget ? null : campaign.pacingPct === null ? (
          // A linked budget's pacingPct is null for any of several reasons the frontend
          // cannot distinguish (period hasn't started, or the budget's amount is not
          // positive), so the pill states the fact and the tooltip carries the several
          // things worth checking - which is too long to sit in every row of a table.
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="secondary">
                {t('performancePacing.pacingRow.pacingUnavailable')}
              </Badge>
            </TooltipTrigger>
            <TooltipContent>
              {t('performancePacing.pacingRow.checkTheBudgetsAmountAndDates')}
            </TooltipContent>
          </Tooltip>
        ) : (
          <div className="flex items-center gap-2">
            {/* Clamped for the bar's own width only - the underlying number, shown
                alongside it, is untouched. Radix draws the fill by shifting a
                full-width indicator, so a value past 100 would push it past the
                track rather than reading as "full". */}
            <Progress
              value={Math.min(campaign.pacingPct, 100)}
              className="w-20"
              aria-label={t('performancePacing.pacingRow.pacingForCampaign', {
                name: campaign.name,
              })}
            />
            <span>{campaign.pacingPct.toFixed(1)}%</span>
          </div>
        )}
      </DatatableCell>
      <DatatableCell>
        {remaining === null ? (
          // Absent because this campaign has no flight end date set, not because a
          // budget or run rate is missing.
          <div className="flex flex-col gap-1">
            <span>{EM_DASH}</span>
            <span className="text-xs text-muted-foreground">
              {t('performancePacing.pacingRow.noFlightEndDateSet')}
            </span>
          </div>
        ) : (
          formatMetric(remaining, 'integer')
        )}
      </DatatableCell>
      <DatatableCell>
        <Badge variant={badge.variant}>
          <badge.Icon aria-hidden />
          {t(badge.labelKey)}
        </Badge>
      </DatatableCell>
    </DatatableRow>
  );
}
